import React, { useState, useRef } from "react";
import { collection } from "../mock";
import { X, Layers } from "lucide-react";
import { useBrochure } from "./BrochureProvider";
import useReveal from "../hooks/useReveal";

function TiltCard({ product, onOpen, delay }) {
  const ref = useRef(null);
  const [style, setStyle] = useState({});

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    const rx = (py - 0.5) * -10;
    const ry = (px - 0.5) * 12;
    setStyle({ transform: `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) scale(1.02)` });
  };
  const onLeave = () => setStyle({ transform: "perspective(900px) rotateX(0) rotateY(0) scale(1)" });

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ ...style, transition: "transform 0.2s ease", animationDelay: delay }}
      className="group relative rounded-3xl bg-white shadow-sm hover:shadow-2xl overflow-hidden border border-slate-100 will-change-transform"
    >
      <div className="h-72 overflow-hidden relative">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111c4e]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {product.tags.map((t) => (
            <span key={t} className="bg-white/90 backdrop-blur text-[#1a2a6b] text-[11px] font-bold px-2.5 py-1 rounded-full">
              {t}
            </span>
          ))}
        </div>
        <button
          onClick={() => onOpen(product)}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all bg-white text-[#1a2a6b] text-sm font-semibold px-5 py-2 rounded-full shadow-lg whitespace-nowrap"
        >
          Quick View
        </button>
      </div>
      <div className="p-5">
        <p className="text-[#2b3aa0] text-xs font-bold uppercase tracking-wide">{product.category}</p>
        <h3 className="text-[#1a2a6b] font-bold text-base mt-1 leading-snug">{product.name}</h3>
        <p className="text-slate-500 text-sm mt-1">{product.fabric}</p>
      </div>
    </div>
  );
}

export default function Collection() {
  const [active, setActive] = useState("All");
  const [quick, setQuick] = useState(null);
  const [ref, inView] = useReveal();
  const brochure = useBrochure();

  const items =
    active === "All"
      ? collection.products
      : collection.products.filter((p) => p.category === active);

  return (
    <section id="collection" className="kse-section bg-white">
      <div className="kse-container">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="kse-eyebrow">
            WHAT WE MANUFACTURE
          </span>
          <h2 className="kse-h2">
            Our Garment Collection
          </h2>
          <p className="text-slate-500 mt-4">
            A curated look at the men's, women's and kids' apparel we produce across knit and woven fabrics.
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap justify-center gap-2.5 mb-12">
          {collection.filters.map((f) => (
            <button
              key={f}
              onClick={() => setActive(f)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-all ${
                active === f
                  ? "bg-[#1a2a6b] text-white border-[#1a2a6b]"
                  : "bg-white text-[#1a2a6b] border-slate-200 hover:border-[#2b3aa0]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div ref={ref} className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map((p, i) => (
            <div key={p.name} className={`reveal ${inView ? "in-view" : ""}`} style={{ animationDelay: `${(i % 4) * 0.08}s` }}>
              <TiltCard product={p} onOpen={setQuick} delay="0s" />
            </div>
          ))}
        </div>
      </div>

      {/* Quick view modal */}
      {quick && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-[#0d1640]/70 backdrop-blur-sm" onClick={() => setQuick(null)} />
          <div className="relative bg-white rounded-3xl w-full max-w-3xl overflow-hidden shadow-2xl grid md:grid-cols-2">
            <div className="h-64 md:h-auto">
              <img src={quick.image} alt={quick.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-7 relative">
              <button onClick={() => setQuick(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700">
                <X size={22} />
              </button>
              <p className="text-[#2b3aa0] text-xs font-bold uppercase tracking-wide">{quick.category}</p>
              <h3 className="text-2xl font-extrabold text-[#1a2a6b] mt-1">{quick.name}</h3>
              <div className="flex items-center gap-2 text-slate-600 mt-3">
                <Layers size={16} className="text-[#2b3aa0]" /> {quick.fabric}
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {quick.tags.map((t) => (
                  <span key={t} className="bg-slate-50 text-[#1a2a6b] text-xs font-bold px-3 py-1.5 rounded-full">{t}</span>
                ))}
              </div>
              <p className="text-slate-500 text-sm mt-5 leading-relaxed">
                Available for bulk manufacturing with custom fabrics, prints, sizing and packing to your specification. Minimum order quantities apply.
              </p>
              <button
                onClick={() => { setQuick(null); brochure?.openBrochure(); }}
                className="kse-btn bg-[#2b3aa0] hover:bg-[#1a2a6b] text-white px-6 py-3 mt-6"
              >
                Enquire &amp; Get Brochure
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
